// 권한 관리 유틸리티 함수들
// src/utils/authUtils.js

/**
 * 사용자 역할 상수
 */
export const USER_ROLES = {
  SUPER: 'SUPER',
  ADMIN: 'ADMIN',
};

/**
 * 사용자 역할 한글명 매핑
 */
export const ROLE_LABELS = {
  [USER_ROLES.SUPER]: '슈퍼관리자',
  [USER_ROLES.ADMIN]: '일반관리자',
};

/**
 * 사용자가 특정 역할을 가지고 있는지 확인
 * @param {Object} user - 사용자 객체
 * @param {string} role - 확인할 역할
 * @returns {boolean}
 */
export const hasRole = (user, role) => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }
  return user.roles.includes(role);
};

/**
 * 사용자가 여러 역할 중 하나라도 가지고 있는지 확인
 * @param {Object} user - 사용자 객체
 * @param {string[]} roles - 확인할 역할 배열
 * @returns {boolean}
 */
export const hasAnyRole = (user, roles) => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }
  return roles.some(role => user.roles.includes(role));
};

/**
 * 사용자가 모든 역할을 가지고 있는지 확인
 * @param {Object} user - 사용자 객체
 * @param {string[]} roles - 확인할 역할 배열
 * @returns {boolean}
 */
export const hasAllRoles = (user, roles) => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }
  return roles.every(role => user.roles.includes(role));
};

/**
 * 슈퍼 관리자인지 확인
 * @param {Object} user - 사용자 객체
 * @returns {boolean}
 */
export const isSuperAdmin = (user) => {
  return hasRole(user, USER_ROLES.SUPER);
};

/**
 * 관리자인지 확인 (일반관리자 또는 슈퍼관리자)
 * @param {Object} user - 사용자 객체
 * @returns {boolean}
 */
export const isAdmin = (user) => {
  return hasAnyRole(user, [USER_ROLES.ADMIN, USER_ROLES.SUPER]);
};

/**
 * 사용자의 최고 권한 역할 반환
 * @param {Object} user - 사용자 객체
 * @returns {string|null}
 */
export const getHighestRole = (user) => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return null;
  }
  
  if (user.roles.includes(USER_ROLES.SUPER)) {
    return USER_ROLES.SUPER;
  }
  
  if (user.roles.includes(USER_ROLES.ADMIN)) {
    return USER_ROLES.ADMIN;
  }
  
  return null;
};

/**
 * 사용자 역할을 한글로 변환
 * @param {string[]} roles - 역할 배열
 * @returns {string}
 */
export const formatRoles = (roles) => {
  if (!roles || !Array.isArray(roles)) {
    return '권한 없음';
  }
  
  return roles
    .map(role => ROLE_LABELS[role] || role)
    .join(', ');
};

/**
 * JWT 토큰에서 역할 정보 디버깅
 * @param {Object} user - 사용자 객체
 */
export const debugUserRoles = (user) => {
  if (process.env.NODE_ENV === 'development') {
    console.group('🔍 사용자 권한 디버깅');
    console.log('사용자 정보:', user);
    console.log('역할 배열:', user?.roles);
    console.log('슈퍼 관리자 여부:', isSuperAdmin(user));
    console.log('관리자 여부:', isAdmin(user));
    console.log('최고 권한:', getHighestRole(user));
    console.log('역할 한글명:', formatRoles(user?.roles));
    console.groupEnd();
  }
};

/**
 * 권한 체크 결과를 반환하는 훅
 * @param {Object} user - 사용자 객체
 * @returns {Object} 권한 체크 결과
 */
export const usePermissions = (user) => {
  return {
    isSuperAdmin: isSuperAdmin(user),
    isAdmin: isAdmin(user),
    hasRole: (role) => hasRole(user, role),
    hasAnyRole: (roles) => hasAnyRole(user, roles),
    hasAllRoles: (roles) => hasAllRoles(user, roles),
    getHighestRole: () => getHighestRole(user),
    formatRoles: () => formatRoles(user?.roles),
    debugRoles: () => debugUserRoles(user),
  };
};